// Stock Adjustment service (operator scope).
//
// Schemas 對齊 app/controllers/api/operator/v1/stock_adjustments_controller.rb 的
// 實際 render block — 不是 BACKEND.md 文件（docs 會 stale，唯一真相在 controller）。
//
// Envelope helpers from @services/apiSchemas — `success`/`message` 由 ApiResponse
// concern 保證，`data` 各 endpoint 形狀不同所以一個 endpoint 一個 schema。
import { z } from 'zod';

import { operatorApiFetch } from './apiService';
import { type ApiErrorEnvelope, apiList, apiSuccess, parseApiResponse } from '@services/apiSchemas';
import {
  PreviewInventoryChangesResponseSchema,
  type PreviewInventoryChangesResponse,
} from '@services/inventoryConflictSchemas';

// ===== Shared shapes =====

// 觸發 worker 的 endpoint 共用：BE 一律回 { job_id: <uuid> } (+ extra)
const JobIdSchema = z.object({ job_id: z.string() }).passthrough();

// ===== Per-endpoint schemas =====

// GET /stock_adjustments/get_records — list row
const StockAdjustmentRecordSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: z.string(),
  note: z.string().nullable(),
  applicant_name: z.string(),
  approver_name: z.string().nullable(),
  created_at: z.string(),
}).passthrough();

// GET /stock_adjustments/:id — show item
const StockAdjustmentItemSchema = z.object({
  id: z.string(),
  inventory_id: z.string().nullable(),
  shelf_id: z.string(),
  product_id: z.string(),
  product_name: z.string(),
  product_sku: z.string(),
  quantity: z.number(),
  reason_code: z.string().nullable(),
  shelf_location: z.string(),
  batch: z.string().nullable(),
  expiration_date: z.string().nullable(),
  manufacturing_date: z.string().nullable(),
  inventory_type_id: z.string().nullable(),
  inventory_type_name: z.string().nullable(),
  custom_attributes: z.record(z.string(), z.unknown()),
  note: z.string().nullable(),
}).passthrough();

const StockAdjustmentDetailSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: z.string(),
  note: z.string().nullable(),
  created_at: z.string(),
  merchant_id: z.string(),
  merchant: z.object({ id: z.string(), name: z.string() }).passthrough(),
  applicant: z.object({ id: z.string(), name: z.string() }).passthrough(),
  approver: z.object({ id: z.string(), name: z.string() }).passthrough().nullable(),
  items: z.array(StockAdjustmentItemSchema),
}).passthrough();

// POST /stock_adjustments — create 只回 { id }
const CreateResultSchema = z.object({ id: z.string() }).passthrough();

// GET /stock_adjustments/suggest_name
const SuggestNameSchema = z.object({ name: z.string() }).passthrough();

// POST /stock_adjustments/import — 回 { job_id, import_id }
const ImportResultSchema = JobIdSchema.extend({ import_id: z.string() }).passthrough();

export type StockAdjustmentRecord = z.infer<typeof StockAdjustmentRecordSchema>;
export type StockAdjustmentDetail = z.infer<typeof StockAdjustmentDetailSchema>;
export type { PreviewInventoryChangesResponse };

export const stockAdjustmentService = {
  // 建立庫存調整單（數量增減）
  create: async (data: Record<string, unknown>) => {
    const res = await operatorApiFetch('stock_adjustments', {
      method: 'POST',
      body: data,
    });
    return parseApiResponse(res, apiSuccess(CreateResultSchema));
  },

  getRecords: async (params: Record<string, unknown>) => {
    const res = await operatorApiFetch('stock_adjustments/get_records', {
      method: 'POST',
      body: params,
    });
    return parseApiResponse(res, apiList(StockAdjustmentRecordSchema));
  },

  get: async (id: string) => {
    const res = await operatorApiFetch(`stock_adjustments/${id}`);
    return parseApiResponse(res, apiSuccess(StockAdjustmentDetailSchema));
  },

  previewApprove: async (id: string) => {
    const res = await operatorApiFetch(`stock_adjustments/${id}/preview_approve`, {
      method: 'POST',
    });
    return parseApiResponse(res, apiSuccess(PreviewInventoryChangesResponseSchema));
  },

  // approve 走 render_success — 無 body。BE 實作上 render_success 仍送 data:null，
  // 用 z.unknown() 留 forward-compat。conflict / race 由 BE raise
  // ADJUSTMENT_EXCEEDS_AVAILABLE 經 ApiErrorEnvelope 回。
  approve: async (id: string) => {
    const res = await operatorApiFetch(`stock_adjustments/${id}/approve`, {
      method: 'POST',
    });
    return parseApiResponse(res, apiSuccess(z.unknown()));
  },

  reject: async (id: string) => {
    const res = await operatorApiFetch(`stock_adjustments/${id}/reject`, {
      method: 'POST',
    });
    return parseApiResponse(res, apiSuccess(z.unknown()));
  },

  update: async (id: string, data: Record<string, unknown>) => {
    const res = await operatorApiFetch(`stock_adjustments/${id}`, {
      method: 'PATCH',
      body: data,
    });
    return parseApiResponse(res, apiSuccess(z.unknown()));
  },

  suggestName: async ({ merchantId }: { merchantId?: string } = {}) => {
    const res = await operatorApiFetch('stock_adjustments/suggest_name', {
      method: 'GET',
      params: merchantId ? { merchant_id: merchantId } : {},
    });
    return parseApiResponse(res, apiSuccess(SuggestNameSchema));
  },

  import: async (formData: FormData) => {
    const res = await operatorApiFetch('stock_adjustments/import', {
      method: 'POST',
      body: formData,
    });
    return parseApiResponse(res, apiSuccess(ImportResultSchema));
  },

  // downloadImportTemplate 回的是 .xlsx binary，不走 envelope；保留原 Response 給
  // caller 處理 blob 下載。
  downloadImportTemplate: (locale: string, merchantId?: string): Promise<Response> => {
    const body: Record<string, unknown> = { type: 'stock_adjustment', locale };
    if (merchantId) {
      body.merchant_id = merchantId;
    }
    return operatorApiFetch('import_templates/download', {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
    });
  },
};

export type { ApiErrorEnvelope };
